import { useContext, useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Tag,
  Tabs,
  Timeline,
  Modal,
  Dropdown,
} from "antd";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import {
  ClockCircleOutlined,
  DownOutlined,
  PlusOutlined,
  UpOutlined,
} from "@ant-design/icons";

import {
  submitLeadNotes,
  resetSubmitLeadNotes,
  emptyErrorObj,
  submitLeadRemarks,
  resetLeadNoteDetails,
  getClientNotesTags,
  getLeadDetails,
  getAllLeadNotes,
  deleteLeadNote,
  deleteLeadRemarks,
  getLeadRemarksDetails,
  getLeadNoteDetails,
} from "../../actionCreator/leadDetailsActionCreator";
import { SAVE_CLIENT_NOTE_ERROR } from "../../actionType/leadDetailsType";
import { useAlert } from "../../context/AlertProvider";
import { AuthContext } from "../../context/AuthContext";
import { subscriptionPlans } from "../../config";
import FeatureComingSoon from "../../common/FeatureComingSoon";
import deleteIcon from "../../assets/images/webToLead/delete.svg";
import editIcon from "../../assets/svg/icons/edit.svg";
import pinIcon from "../../assets/svg/icons/pin.svg";
import starIcon from "../../assets/svg/icons/star.svg";
import { ReactComponent as Note } from "../../assets/svg/icons/notes.svg";
import { FileText, Star } from "lucide-react";
import AttractiveEmptyState from "../../common/AttractiveEmptyState";
import { useParams } from "react-router";
import { formatSmartDate } from "../../utils";
import collapse from "../../assets/svg/icons/collapse-activities.svg";
import { ReactComponent as EditIcon } from "../../assets/svg/icons/edit-action.svg";
import { ReactComponent as TrashIcon } from "../../assets/svg/icons/trash-action.svg";
import { ReactComponent as PinIcon } from "../../assets/svg/icons/pin-action.svg";
import { ReactComponent as StarIcon } from "../../assets/svg/icons/star-action.svg";
import { ReactComponent as ChevronDownIcon } from "../../assets/svg/icons/chevron-down-action.svg";
const TagIcon = ({ className }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M13.125 3.75024C13.7463 3.75024 14.25 4.25392 14.25 4.87524C14.25 5.49656 13.7463 6.00024 13.125 6.00024C12.5037 6.00024 12 5.49656 12 4.87524C12 4.25392 12.5037 3.75024 13.125 3.75024Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2.08067 8.35792C1.32831 9.19822 1.31213 10.4659 2.00262 11.3578C3.37283 13.1275 4.87256 14.6272 6.64225 15.9973C7.53405 16.6879 8.80178 16.6717 9.64208 15.9193C11.9234 13.8766 14.0126 11.7419 16.0289 9.39592C16.2283 9.16402 16.3529 8.87978 16.3809 8.5752C16.5047 7.22849 16.7589 3.3485 15.7052 2.2948C14.6515 1.24111 10.7715 1.49533 9.4248 1.61907C9.12023 1.64706 8.83598 1.77175 8.604 1.97108C6.25809 3.98734 4.12336 6.07658 2.08067 8.35792Z"
      stroke="currentColor"
    />
    <path
      d="M5.25 10.5002L7.49998 12.7502"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const { TextArea } = Input;

const Notes = ({ setActiveTab }) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [form] = Form.useForm();
  const { confirm } = Modal;
  const { showAlert } = useAlert();
  const { subscription } = useContext(AuthContext);
  const tags = useSelector((state) => state.LeadDetailsReducer?.notesTags);
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [showHistory, setShowHistory] = useState(true);
  const leadNotes = useSelector((state) => state.LeadDetailsReducer.leadNotes);
  useEffect(() => {
    if (id) {
      dispatch(getAllLeadNotes(id));
    }
    dispatch(getClientNotesTags());
  }, [dispatch, id]);

  const errObj = useSelector((state) => state.LeadDetailsReducer.errObj);
  const leadDetail = useSelector(
    (state) => state.LeadDetailsReducer.leadDetails,
  );
  const leadNotesSubmitted = useSelector(
    (state) => state.LeadDetailsReducer.saveleadNotes,
  );
  const leadNoteDetails = useSelector(
    (state) => state.LeadDetailsReducer.leadNoteDetails,
  );

  useEffect(() => {
    if (!!Object.keys(errObj).length) {
      showAlert("error", errObj[SAVE_CLIENT_NOTE_ERROR]?.message);
      dispatch(resetSubmitLeadNotes());
      dispatch(emptyErrorObj());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errObj]);

  useEffect(() => {
    if (Object.keys(leadNotesSubmitted).length) {
      showAlert(
        "success",
        leadNoteDetails?.id
          ? "Note has been updated!"
          : "Note has been submitted!",
      );
      dispatch(resetSubmitLeadNotes());
      dispatch(emptyErrorObj());
      form.resetFields();
      setSelected([]);
      setSearchValue("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadNotesSubmitted]);

  useEffect(() => {
    if (!Object.keys(leadNoteDetails).length) return;
    form.setFieldsValue({
      title: leadNoteDetails?.title,
      note: leadNoteDetails?.note,
      tags: leadNoteDetails?.tags?.map((d) => d?.name),
    });
  }, [leadNoteDetails, form]);

  useEffect(() => {
    dispatch(getClientNotesTags());
  }, [dispatch]);

  useEffect(() => {
    setOptions(tags?.map((tag) => tag.name));
  }, [tags]);

  const redirectToTimline = () => {
    setActiveTab("1");
    form.resetFields();
    dispatch(resetLeadNoteDetails());
  };

  const getActiveTab = (id, type) => {
    if (type === "deletenote") {
      showConfirm(id, leadDetail?.id, "note");
    } else {
      if (type === "editnote") {
        dispatch(getLeadNoteDetails(id));
      } else if (type === "editremarks") {
        dispatch(getLeadRemarksDetails(id));
      }
    }
  };

  function showConfirm(id, leadId, type) {
    confirm({
      title: "Do you want to delete this " + type + "?",
      content: "",
      onOk() {
        return new Promise((resolve, reject) => {
          setTimeout(Math.random() > 0.5 ? resolve : reject, 1000);
          if (type === "note") {
            dispatch(deleteLeadNote(id, leadId));
          } else {
            dispatch(deleteLeadRemarks(id, leadId));
          }
        }).catch(() => console.log("Oops errors!"));
      },
      onCancel() {},
    });
  }

  const handleDelete = (id) => {
    getActiveTab(id, "deletenote");
  };

  const saveNotes = async (values) => {
    const payload = {
      title: values.title,
      note: values.note,
      notefor: values.noteFor,
      tags: values?.tags?.map((d) => ({ name: d })),
      isActive: values.isActive,
      isTask: values.isTask,
      lead: { id: leadDetail?.id },
      update: !!Object.keys(leadNoteDetails).length,
      id: leadNoteDetails?.id || "",
    };

    const res = await dispatch(submitLeadNotes(payload));

    if (res?.id) {
      dispatch(getLeadDetails(leadDetail?.id));
    }
    if (!payload.update && payload.isTask) {
      const formattedDate = new Date().toISOString();
      const remarksPayload = {
        remarks: values.noteFor,
        interactionDate: formattedDate,
        isContactBackRequired: false,
        isContacted: false,
        isToDisplay: true,
        status: "open",
        title: values.note,
        lead: leadDetail,
        update: false,
        id: "",
      };
      dispatch(submitLeadRemarks(remarksPayload));
    }

    form.resetFields();
  };

  const timelineItems = leadNotes?.map((item) => {
    const rawTime = item.lastUpdated;
    const formattedTime = formatSmartDate(rawTime);
    return {
      key: item.id,
      data: item,
      dot: (
        <div className="tw-rounded-full tw-text-text-primary tw-border tw-border-slate-200 tw-bg-secondary-color tw-border-solid tw-p-2">
          <Note />
        </div>
      ),
      children: (
        <NoteCard
          item={item}
          handleDelete={handleDelete}
          formattedTime={formattedTime}
          onEdit={() => {
            form.setFieldsValue({
              title: item?.title,
              note: item?.note,
              tags: item?.tags?.map((d) => d?.name),
            });
            setSelected(item?.tags?.map((d) => d?.name));
            getActiveTab(item?.id, "editnote");
            setActiveTab("2");
            document.getElementById("addNote").scrollIntoView({
              behavior: "smooth",
              top: 0,
            });
          }}
        />
      ),
    };
  });

  return (
    <>
      <div className="tw-p-6 tw-bg-white">
        <div className="errorformcontainer">
          <Form id="addNote" form={form} layout="vertical" onFinish={saveNotes}>
            <Row className="justify-content-between">
              <Col span={24}>
                <Form.Item
                  name="title"
                  rules={[{ required: true, message: "Please enter a title" }]}
                  className="mb15"
                >
                  <Input
                    placeholder="Add Title to your note..."
                    className="!tw-border-slate-200/80 focus:!tw-border-theme focus:!tw-shadow-[0_0_0_2px_rgba(99,102,241,0.1)] !tw-rounded-xl tw-py-2.5 tw-px-3.5 tw-font-semibold tw-text-slate-800 placeholder:tw-text-slate-400 tw-transition-all"
                  />
                </Form.Item>

                <Form.Item
                  name="note"
                  className="mb15"
                  rules={[
                    { required: true, message: "Please enter a note" },
                    { min: 10, message: "Note must be at least 10 characters" },
                    { max: 255, message: "Note can’t exceed 255 characters." },
                  ]}
                >
                  <TextArea
                    rows={4}
                    maxLength={500}
                    placeholder="Type your note here..."
                    className="!tw-bg-amber-100/40 !tw-border-slate-200/80 focus:!tw-border-theme focus:!tw-shadow-[0_0_0_2px_rgba(99,102,241,0.1)] !tw-rounded-xl tw-p-3.5 tw-text-slate-700 placeholder:tw-text-slate-400 tw-transition-all"
                  />
                </Form.Item>

                {![
                  subscriptionPlans?.trail,
                  subscriptionPlans?.starter,
                ].includes(subscription?.subscriptionPackage?.name) && (
                  <Form.Item
                    label={
                      <label className="tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider">
                        Tags
                      </label>
                    }
                    name="tags"
                    className="mb15"
                  >
                    <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                      <Input
                        value={searchValue}
                        placeholder="Add Tag..."
                        className="!tw-w-[160px] !tw-h-9 !tw-border-slate-200/80 focus:!tw-border-theme focus:!tw-shadow-[0_0_0_2px_rgba(99,102,241,0.1)] !tw-rounded-xl tw-px-3 tw-text-xs placeholder:tw-text-slate-450 tw-transition-all"
                        onChange={(e) => setSearchValue(e.target.value)}
                      />
                      <button
                        type="button"
                        className="tw-size-9 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-border tw-border-solid tw-border-slate-200 hover:tw-border-theme hover:tw-text-theme tw-bg-slate-50/50 hover:tw-bg-indigo-50/20 tw-rounded-xl tw-transition-all tw-text-slate-500 tw-outline-none"
                        onClick={() => {
                          if (options.includes(searchValue)) {
                            showAlert(
                              "warning",
                              "This tag already exists. Please select it from the list on the right.",
                            );
                          } else if (!searchValue) {
                            showAlert("warning", "Please enter tag name.");
                          } else {
                            setOptions((prev) => [searchValue, ...prev]);
                            setSelected([searchValue, ...selected]);
                            form.setFieldsValue({
                              tags: [searchValue, ...selected],
                            });
                            setSearchValue("");
                          }
                        }}
                      >
                        <PlusOutlined className="tw-text-xs" />
                      </button>

                      {options?.slice(0, 3).map((tag) => (
                        <Tag
                          key={tag}
                          className={`tw-cursor-pointer tw-py-1.5 tw-px-3.5 tw-rounded-full tw-font-semibold tw-text-xs tw-border tw-border-solid tw-m-0 tw-transition-all tw-flex tw-items-center tw-gap-1.5 ${
                            selected?.includes(tag)
                              ? "tw-bg-indigo-50/40 tw-border-theme/40 tw-text-theme"
                              : "tw-bg-white tw-border-slate-200 tw-text-slate-500 hover:tw-border-slate-350"
                          }`}
                          onClick={() => {
                            if (selected?.includes(tag)) {
                              setSelected([
                                ...selected?.filter((d) => d !== tag),
                              ]);
                              form.setFieldsValue({
                                tags: [...selected?.filter((d) => d !== tag)],
                              });
                            } else {
                              setSelected([...selected, tag]);
                              form.setFieldsValue({
                                tags: [...selected, tag],
                              });
                            }
                          }}
                        >
                          <TagIcon className="tw-size-3.5 tw-shrink-0" />
                          <span>{tag}</span>
                        </Tag>
                      ))}

                      {options?.length > 3 && (
                        <Dropdown
                          trigger={["click", "hover"]}
                          placement="bottomLeft"
                          overlay={
                            <div className="tw-w-[260px] tw-bg-white tw-rounded-xl tw-shadow-lg tw-p-3 tw-border tw-border-solid tw-border-slate-100">
                              <div className="tw-font-bold tw-text-xs tw-mb-2.5 tw-text-slate-500 tw-uppercase tw-tracking-wider tw-text-center">
                                All Tags ({options?.slice(3).length})
                              </div>
                              <div className="tw-max-h-[300px] tw-pr-1 tw-overflow-y-auto custom-scrollbar tw-flex tw-flex-col tw-gap-1.5">
                                {options?.slice(3).map((item, index) => (
                                  <div
                                    key={index}
                                    className={`tw-flex tw-justify-between tw-items-center tw-p-2 tw-rounded-xl tw-cursor-pointer tw-border tw-border-solid tw-transition-all ${
                                      selected?.includes(item)
                                        ? "tw-bg-indigo-50/40 tw-border-theme/40 tw-text-theme tw-font-semibold"
                                        : "tw-bg-white tw-border-slate-100 hover:tw-border-slate-200 tw-text-slate-650"
                                    }`}
                                    onClick={() => {
                                      if (selected?.includes(item)) {
                                        setSelected([
                                          ...selected?.filter(
                                            (d) => d !== item,
                                          ),
                                        ]);
                                        form.setFieldsValue({
                                          tags: [
                                            ...selected?.filter(
                                              (d) => d !== item,
                                            ),
                                          ],
                                        });
                                      } else {
                                        setSelected([...selected, item]);
                                        form.setFieldsValue({
                                          tags: [...selected, item],
                                        });
                                      }
                                    }}
                                  >
                                    <span className="tw-text-xs tw-flex tw-items-center tw-gap-2">
                                      <TagIcon className="tw-size-3.5" />
                                      <span>{item}</span>
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          }
                        >
                          <Tag className="tw-cursor-pointer tw-py-1.5 tw-px-3.5 tw-rounded-full tw-font-semibold tw-text-xs tw-border tw-border-solid tw-bg-white tw-border-slate-200 tw-text-slate-500 hover:tw-border-slate-350 tw-m-0">
                            <span className="tw-cursor-pointer tw-w-fit">
                              + {options?.slice(3).length} more
                            </span>
                          </Tag>
                        </Dropdown>
                      )}
                    </div>
                  </Form.Item>
                )}
              </Col>
            </Row>

            <div className="tw-flex tw-items-center tw-justify-between tw-pt-4 tw-mt-4 tw-border-0 tw-border-t tw-border-solid tw-border-slate-100">
              <div className="tw-flex tw-items-center tw-gap-2" />
              <Button
                type="primary"
                htmlType="submit"
                className="!tw-bg-theme tw-font-bold !tw-outline-0 !tw-border-theme !tw-text-white tw-text-xs tw-h-9 tw-px-5 tw-rounded-xl tw-shadow-sm hover:tw-shadow tw-transition-all"
              >
                {leadNoteDetails?.id ? "Update Note" : "Save Note"}
              </Button>
            </div>
          </Form>
        </div>
      </div>

      <div className="tw-p-6 tw-bg-slate-50/50 tw-border-0 tw-border-t tw-border-solid tw-border-slate-100">
        <div
          className="tw-flex tw-items-center tw-justify-between tw-cursor-pointer tw-mb-4 tw-select-none"
          onClick={() => setShowHistory(!showHistory)}
        >
          <div className="tw-text-sm tw-font-bold tw-text-slate-800 tw-flex tw-items-center tw-gap-2">
            <span>History</span>
            <span className="tw-text-[10px] tw-font-bold tw-bg-slate-200/70 tw-text-slate-500 tw-px-2 tw-py-0.5 tw-rounded-full">
              {timelineItems?.length || 0} Notes
            </span>
          </div>
          <div className="tw-size-7 tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-flex tw-items-center tw-justify-center tw-text-slate-450 hover:tw-text-theme tw-transition-colors">
            {showHistory ? (
              <UpOutlined className="tw-text-[10px]" />
            ) : (
              <DownOutlined className="tw-text-[10px]" />
            )}
          </div>
        </div>

        {showHistory && (
          <Tabs
            className="activities-tab !tw-border-none !tw-bg-transparent !tw-shadow-none"
            items={[
              {
                key: "1",
                label: (
                  <span className="tw-px-4 tw-py-1 tw-text-xs tw-font-bold">
                    All Notes ({timelineItems?.length || 0})
                  </span>
                ),
                children: timelineItems?.length ? (
                  <Timeline className="tw-mt-5" items={timelineItems} />
                ) : (
                  <AttractiveEmptyState
                    icon={FileText}
                    title="No notes recorded"
                    description="Notes created for this lead will appear here."
                  />
                ),
              },
              {
                key: "2",
                label: (
                  <FeatureComingSoon>
                    <span className="tw-px-4 tw-py-1 tw-text-xs tw-font-bold">
                      Starred
                    </span>
                  </FeatureComingSoon>
                ),
                children: (
                  <AttractiveEmptyState
                    icon={Star}
                    title="No starred notes"
                    description="Key notes you star will show up in this filter."
                  />
                ),
              },
              {
                key: "3",
                label: (
                  <span className="tw-px-4 tw-py-1 tw-text-xs tw-font-bold">
                    Today (
                    {
                      timelineItems?.filter((data) => {
                        return moment(data?.data?.lastUpdated).isSame(
                          moment(),
                          "day",
                        );
                      })?.length
                    }
                    )
                  </span>
                ),
                children: timelineItems?.filter((data) => {
                  return moment(data?.data?.lastUpdated).isSame(
                    moment(),
                    "day",
                  );
                })?.length ? (
                  <Timeline
                    className="tw-mt-5"
                    items={timelineItems?.filter((data) => {
                      return moment(data?.data?.lastUpdated).isSame(
                        moment(),
                        "day",
                      );
                    })}
                  />
                ) : (
                  <AttractiveEmptyState
                    icon={FileText}
                    title="No notes added today"
                    description="Notes created today will appear here."
                  />
                ),
              },
            ]}
          />
        )}
      </div>
    </>
  );
};

export default Notes;

const NoteCard = ({ item, handleDelete, formattedTime, onEdit }) => {
  const [isCollapse, setCollapse] = useState(false);

  return (
    <div className="tw-ml-4 tw-group tw-bg-white hover:tw-shadow-sm tw-border tw-border-solid tw-border-slate-200/80 tw-py-4 tw-px-5 tw-mt-4 tw-rounded-2xl tw-transition-all">
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-mb-2">
        <div className="tw-font-bold tw-text-sm tw-text-slate-800 tw-leading-snug">
          {item?.title || item?.note}
        </div>
        <div className="tw-flex tw-items-center tw-gap-1.5 tw-shrink-0">
          <div className="tw-opacity-0 group-hover:tw-opacity-100 tw-flex tw-items-center tw-justify-end tw-gap-1.5 tw-transition-opacity tw-duration-150">
            <button
              type="button"
              className="tw-flex tw-items-center tw-justify-center tw-size-8 tw-rounded-full tw-border tw-border-solid tw-border-slate-200 hover:tw-border-blue-400 hover:tw-text-blue-500 hover:tw-bg-blue-50/30 tw-bg-white tw-text-slate-450 tw-cursor-pointer tw-transition-all tw-outline-none"
              onClick={onEdit}
            >
              <EditIcon className="tw-size-3.5" />
            </button>

            <button
              type="button"
              className="tw-flex tw-items-center tw-justify-center tw-size-8 tw-rounded-full tw-border tw-border-solid tw-border-slate-200 hover:tw-border-rose-350 hover:tw-text-rose-500 hover:tw-bg-rose-50/30 tw-bg-white tw-text-slate-450 tw-cursor-pointer tw-transition-all tw-outline-none"
              onClick={() => handleDelete(item.id)}
            >
              <TrashIcon className="tw-size-3.5" />
            </button>

            <FeatureComingSoon>
              <button
                type="button"
                className="tw-flex tw-items-center tw-justify-center tw-size-8 tw-rounded-full tw-border tw-border-solid tw-border-slate-200 hover:tw-border-slate-350 hover:tw-text-slate-750 hover:tw-bg-slate-50 tw-bg-white tw-text-slate-450 tw-cursor-pointer tw-transition-all tw-outline-none"
              >
                <PinIcon className="tw-size-3.5" />
              </button>
            </FeatureComingSoon>
          </div>

          <FeatureComingSoon>
            <button
              type="button"
              className="tw-flex tw-items-center tw-justify-center tw-size-8 tw-rounded-full tw-border tw-border-solid tw-border-slate-200 hover:tw-border-amber-355 hover:tw-text-amber-500 hover:tw-bg-amber-50/40 tw-bg-white tw-text-slate-450 tw-cursor-pointer tw-transition-all tw-outline-none"
            >
              <StarIcon className="tw-size-3.5" />
            </button>
          </FeatureComingSoon>

          <button
            type="button"
            className="tw-flex tw-items-center tw-justify-center tw-size-8 tw-rounded-full tw-border tw-border-solid tw-border-slate-200 hover:tw-border-slate-350 hover:tw-text-slate-755 hover:tw-bg-slate-50 tw-bg-white tw-text-slate-450 tw-cursor-pointer tw-transition-all tw-outline-none"
            onClick={() => setCollapse(!isCollapse)}
          >
            <ChevronDownIcon
              className={`tw-size-3.5 tw-transition-transform tw-duration-200 ${isCollapse ? "tw-rotate-180" : ""}`}
            />
          </button>
        </div>
      </div>

      {!isCollapse && (
        <div className="tw-transition-all">
          <p className="tw-text-xs tw-text-slate-500 tw-leading-relaxed tw-whitespace-pre-line tw-mb-4 tw-mt-1">
            {item?.note}
          </p>
          <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-slate-100 tw-pt-3">
            <div className="tw-flex tw-items-center tw-text-[11px] tw-text-slate-400 tw-gap-1.5">
              <ClockCircleOutlined className="tw-text-slate-400" />
              <span>{formattedTime}</span>
            </div>

            {!!item?.tags?.length && (
              <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-1">
                {item?.tags?.map((tag) => (
                  <Tag
                    key={tag?.name}
                    className="tw-rounded-full tw-bg-slate-50 tw-border tw-border-solid tw-border-slate-200/60 tw-text-[10px] tw-font-semibold tw-text-slate-500 tw-py-0.5 tw-px-2 tw-m-0"
                  >
                    {tag?.name}
                  </Tag>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
